// The loader runs its own CSS choreography and the last keyframe, `loader-letter`, only settles at
// 2.1s. Anything shorter cuts the animation mid-way, so the floor is the same on every device.
const MINIMUM_VISIBLE_MS = 2_000;
const MAXIMUM_WAIT_MS = 8_000;
const FADE_DURATION_MS = 700;

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export class LoaderController {
  readonly #element: HTMLElement;
  readonly #startedAt = performance.now();
  #complete = false;
  #fadeOut?: Promise<void>;

  constructor(element: HTMLElement) {
    this.#element = element;
    document.body.classList.add("is-loading");
  }

  async finish(viewReady: Promise<void>): Promise<void> {
    if (this.#complete) return;
    this.#complete = true;

    const fontsReady = "fonts" in document ? document.fonts.ready.then(() => undefined) : Promise.resolve();
    const readiness = Promise.allSettled([fontsReady, viewReady]).then(() => undefined);
    const timeout = delay(MAXIMUM_WAIT_MS);
    const minimum = delay(Math.max(0, MINIMUM_VISIBLE_MS - (performance.now() - this.#startedAt)));

    await Promise.all([Promise.race([readiness, timeout]), minimum]);
    this.#element.classList.remove("is-loading");
    this.#element.classList.add("is-loaded");
    this.#element.setAttribute("aria-hidden", "true");
    document.body.classList.remove("is-loading");
    this.#fadeOut = delay(FADE_DURATION_MS).then(() => {
      this.#element.hidden = true;
      document.documentElement.dataset.appReady = "true";
    });
  }

  /**
   * Resolves once the curtain is gone. `finish` deliberately returns while it is still fading so
   * the view entrance overlaps the fade instead of queueing behind it.
   */
  get faded(): Promise<void> {
    return this.#fadeOut ?? Promise.resolve();
  }
}
